import { useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { Menu, X } from "lucide-react"

import BrandLockup from "@/components/tuw/auth/BrandLockup"
import { Button } from "@/components/ui/button"
import { visitError } from "@/lib/viewingRoom/guest"
import { VR } from "@/lib/viewingRoom/wording"

// Viewing Room: the signed-out header and frame for freelancer search and
// profiles. Header links are plain, full document loads. A guest visit inside
// the frame that fails (opening a profile from a result, say) shows the
// approved words here instead of Inertia's error page; the search's own
// requests handle their failures in place and never reach this. Header and
// page content stay within FRAME, centred, so wide screens don't stretch them.
const LINKS = [
  { href: "/find-freelancers", label: VR.nav.findFreelancers },
  { href: "/users/sign_in", label: VR.nav.signIn },
]

const FRAME = "mx-auto w-full max-w-[1200px]"

export default function GuestShell({ children }) {
  const [open, setOpen] = useState(false)
  const [failure, setFailure] = useState(null)
  const lastVisit = useRef(null)

  useEffect(() => {
    const offs = [
      router.on("before", (event) => {
        lastVisit.current = event.detail.visit.url?.href ?? event.detail.visit.url
      }),
      router.on("start", () => setFailure(null)),
      router.on("httpException", (event) => {
        event.preventDefault()
        setFailure(visitError(event.detail.response, VR.error.profile))
      }),
      router.on("networkError", (event) => {
        event.preventDefault()
        setFailure({ body: VR.error.profile })
      }),
    ]
    return () => offs.forEach((off) => off())
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-[60] border-b border-border bg-card">
        <div className={`${FRAME} flex items-center gap-[18px] px-[26px] py-[14px] max-[861px]:px-[14px]`}>
          <a href="https://theurbanwriters.com" aria-label={VR.nav.homeLabel} className="shrink-0">
            <BrandLockup className="h-[28px] max-[561px]:h-6" />
          </a>
          <div className="flex-1" />
          <nav className="flex items-center gap-[18px] max-[861px]:hidden">
            {LINKS.map((link) => (
              <a key={link.href} href={link.href} className="text-[13.5px] font-semibold text-muted-foreground hover:text-foreground">
                {link.label}
              </a>
            ))}
            <Button asChild>
              <a href="/post-project">{VR.nav.postFree}</a>
            </Button>
          </nav>
          <button
            type="button"
            className="hidden rounded-[10px] border border-border p-[8px] max-[861px]:flex"
            aria-label={open ? VR.nav.closeMenu : VR.nav.openMenu}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="size-5" strokeWidth={2} /> : <Menu className="size-5" strokeWidth={2} />}
          </button>
        </div>
        {open && (
          <nav className="hidden flex-col gap-[4px] border-t border-border px-[14px] py-[12px] max-[861px]:flex">
            {LINKS.map((link) => (
              <a key={link.href} href={link.href} className="rounded-[9px] px-[10px] py-[10px] text-[14px] font-semibold">
                {link.label}
              </a>
            ))}
            <Button asChild className="mt-[6px] justify-center">
              <a href="/post-project">{VR.nav.postFree}</a>
            </Button>
          </nav>
        )}
      </header>
      {failure && (
        <div role="alert" className="mx-auto mt-[16px] w-[calc(100%-52px)] max-w-[1148px] rounded-[12px] border border-border bg-card px-[16px] py-[14px] text-[13px] leading-[1.5] max-[861px]:w-[calc(100%-28px)]">
          {failure.title && <div className="font-display font-bold text-foreground">{failure.title}</div>}
          <div className="text-ink-3">{failure.body}</div>
          {lastVisit.current && (
            <Button variant="outline" size="sm" className="mt-[10px]" onClick={() => router.visit(lastVisit.current)}>
              {VR.error.retry}
            </Button>
          )}
        </div>
      )}
      <main className={FRAME}>{children}</main>
    </div>
  )
}
